<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import { storeToRefs } from "pinia";
import { nextTick, ref, watch } from "vue";
import ArrowLeft from "#/assets/iconsDynamic/ArrowLeft.vue";
import Close from "#/assets/iconsDynamic/Close.vue";
import { usePdf } from "#/composables/usePdfMode";
import { SINGLE_MODIFIERS_GROUP_ID } from "#/constants/modifiers";
import { vDragScroll } from "#/directives/vDragScroll";
import { vFlyTo } from "#/directives/vFlyTo";
import { vMuted } from "#/directives/vMuted";
import { useCardStore } from "#/store/card";
import { useMainStore } from "#/store/main";
import { useModulesStore } from "#/store/modules";
import { useThemeStore } from "#/store/theme";
import type { ISelectModifiers } from "#/types/iCardProduct";
import { formatPrice, getLocaleName } from "#/utils/i18next";

const { t, i18next } = useTranslation();
const emit = defineEmits<{
	(e: "close"): void;
	(e: "add-to-cart"): void;
	(e: "open-combo"): void;
}>();

const themeStore = useThemeStore();
const mainStore = useMainStore();
const cardStore = useCardStore();
const modulesStore = useModulesStore();

const { stateCard, recountGroupTotalAmount, groupTotalAmount, isLastGroup } = storeToRefs(cardStore);
const { isDesktop, shouldUseDragScroll } = usePdf();

const following = () => {
	stateCard.value.isSectionModifier = true;
	const currentGroup = stateCard.value.groupCurrentOpen;
	if (!currentGroup) return;

	const groupIndex = stateCard.value.groupModifiers.findIndex((g) => g.id === currentGroup.id);
	const result = cardStore.validateGroup(groupIndex);

	if (!result.valid) {
		stateCard.value.textErrorRequaired = result.errorText!;
		modulesStore.showNotification(stateCard.value.textErrorRequaired);
		return;
	}

	if (isLastGroup.value) {
		const product = stateCard.value.openCard;

		const hasCombo = !!(
			product &&
			((Array.isArray((product as any).combos) && (product as any).combos.length > 0) ||
				(Array.isArray((product as any).combo) && (product as any).combo.length > 0) ||
				(product as any).combo_id != null)
		);

		if (hasCombo) {
			emit("open-combo");
		} else {
			emit("add-to-cart");
		}
	} else {
		cardStore.toggleNotRequire(currentGroup, undefined, true);
	}
};

const getModifierPrice = (modifierGroup: ISelectModifiers) => {
	return modifierGroup.sizes?.find((size) => size.id === stateCard.value.selectedSize.id)?.price || modifierGroup.price || 0;
};

const modifierBody = ref<HTMLElement | null>(null);

watch(
	() => stateCard.value.groupCurrentOpen?.id,
	() => {
		nextTick(() => {
			const scroller = modifierBody.value?.querySelector<HTMLElement>(".wrapper_modifiers_card, .wrapper_modifiers_card_column");
			if (!scroller) return;
			scroller.scrollLeft = 0;
			scroller.scrollTop = 0;
		});
	},
);
</script>

<template>
	<div class="wrapper_modifiers">
		<div class="wrapper_nutritional_value">
			<div class="card_header_inner_back" @click="emit('close')">
				<ArrowLeft class="arrow_bottom" />
			</div>
			<div
				class="card_header_inner_back"
				:style="{ marginRight: '10px' }"
				@click="emit('close')"
			>
				<Close class="cross_bottom" />
			</div>
		</div>
		<div ref="modifierBody" class="modifier_shadow">
			<p class="wrapper_modifiers_title">{{ getLocaleName(stateCard.groupCurrentOpen, i18next.language) }}</p>
			<p class="wrapper_text_count_modifiers">{{ t(`card.modifiers.count`, { selected: recountGroupTotalAmount, total: groupTotalAmount }) }}</p>
			<div
				:class="
          stateCard.groupModifiers.find((g) => g.id === stateCard.groupCurrentOpen?.id)?.group_modifiers.length || 0 > 2
            ? 'wrapper_modifiers_card'
            : 'wrapper_modifiers_card_column'
        "
				v-drag-scroll="
          stateCard.groupModifiers.find((g) => g.id === stateCard.groupCurrentOpen?.id)?.group_modifiers.length || (0 > 2 && shouldUseDragScroll) ? {} : false
        "
			>
				<div
					v-for="modifierGroup in stateCard.groupCurrentOpen?.group_modifiers || []"
					:key="modifierGroup.id"
					:id="modifierGroup.id"
					:style="{ pointerEvents: isDesktop ? 'none' : 'auto' }"
					class="modifier_card"
					tabindex="0"
					role="button"
					data-clickable="true"
					:class="{
            hidden_modifier_card:
              modifierGroup.default_amount === 0 &&
              stateCard.groupCurrentOpen?.id === SINGLE_MODIFIERS_GROUP_ID &&
              modifierGroup.min_amount === 0 &&
              modifierGroup.max_amount === 0,
          }"
					v-fly-to="{
            target: `.card_product_modifiers_sheet_body #scrolling${stateCard?.groupCurrentOpen?.id} .modified_plus_btn`,
            scroll: true,
            canAnimate: () => stateCard.isAnimationFly,
          }"
					@click="cardStore.modifierSelect(modifierGroup)"
				>
					<div
						:class="{
              modifier_card_active: stateCard.groupModifiersSelected.some(
                (g) => g.group === stateCard.groupCurrentOpen?.id && g.modifiers.some((m) => m.id === modifierGroup.id)
              ),
            }"
						class="wrapper_image_corners"
					>
						<div class="counter_modifier">
							<span v-if="modifierGroup.amount > 1">{{ modifierGroup.amount }}</span>
							<span v-else class="counter_checkmark" aria-hidden="true"></span>
						</div>
						<div class="hide_image_corners">
							<img
								v-if="!modifierGroup.video_link"
								class="modifier_image"
								:src="modifierGroup.image_link || themeStore.data?.fallback_image || ''"
								:alt="modifierGroup.name"
								loading="lazy"
							>
							<video v-else loop autoplay muted v-muted playsinline class="card__video">
								<source :src="modifierGroup.video_link || ''" type="video/webm">
							</video>
							<div class="wrapper_modifier_title" :class="{ wrapper_modifier_title_required: modifierGroup.min_amount > 0 || modifierGroup.required }">
								<h3 class="modifier_title">{{ getLocaleName(modifierGroup, i18next.language) }}</h3>
								<p class="modifier_price">+<span>{{ formatPrice(getModifierPrice(modifierGroup), mainStore?.config_lib?.currency_symbol) }}</span></p>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div v-if="mainStore.config_lib?.enable_sequential_modifiers" class="modifier_continue_or_add_wrapper">
				<button type="button" @click="following()" class="modifier_continue_or_add">{{ isLastGroup ? t(`card.button.add`) : t(`card.button.continue`) }}</button>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
  @use './sectionModifiers.scss';
</style>
